import type { CollectionAccess, CollectionRole, Member, Root, TaskAccess, TaskRole } from "../../domain/app-types.ts";
import type { StoredEntity } from "../../infrastructure/storage/indexed-db.ts";
import { accessScopeKey, emailForMember, entityVisibleInAccessScope, matchesTaskAccessRole, membersForRoot, membersForTask } from "./access-policy.ts";

export interface WorkspaceAccessSnapshot {
  personalRootId: string | null;
  taskOnlyRootIds: Set<string>;
  collectionRootIds: Set<string>;
  taskAccess: TaskAccess[];
  collectionAccess: CollectionAccess[];
  outgoingSharedTaskIds: Set<string>;
  directory: Member[];
}

export class WorkspaceAccessState {
  personalRootId: string | null = null;
  taskOnlyRootIds = new Set<string>();
  collectionRootIds = new Set<string>();
  taskRoles = new Map<string, TaskRole>();
  collectionRoles = new Map<string, CollectionRole>();
  sharedTaskIds = new Set<string>();
  outgoingSharedTaskIds = new Set<string>();
  directory: Member[] = [];

  restore(snapshot: WorkspaceAccessSnapshot): void {
    this.personalRootId = snapshot.personalRootId;
    this.taskOnlyRootIds = snapshot.taskOnlyRootIds;
    this.outgoingSharedTaskIds = snapshot.outgoingSharedTaskIds;
    this.directory = snapshot.directory;
    this.setTaskAccess(snapshot.taskAccess);
    this.setCollectionAccess(snapshot.collectionAccess);
  }

  setTaskAccess(access: TaskAccess[]): void {
    this.taskRoles = new Map(access.map(item => [item.id, item.role]));
    this.sharedTaskIds = new Set(this.taskRoles.keys());
  }

  setCollectionAccess(access: CollectionAccess[]): void {
    this.collectionRoles = new Map(access.map(item => [item.id, item.role]));
    this.collectionRootIds = new Set(access.map(item => item.root_id));
  }

  scopeKey(roots: Root[]): string {
    return accessScopeKey(roots, this.taskRoles, this.collectionRoles);
  }

  entityVisible(entity: StoredEntity): boolean {
    return entityVisibleInAccessScope(entity, this.taskOnlyRootIds, this.collectionRootIds, this.taskRoles, this.collectionRoles);
  }

  members(rootId: string | null): Member[] {
    return membersForRoot(this.directory, rootId);
  }

  taskMembers(rootId: string | null, collectionId: string | null): Member[] {
    return membersForTask(this.directory, rootId, collectionId);
  }

  memberEmail(id: string): string {
    return emailForMember(this.directory, id);
  }

  taskHasRole(taskId: string, roles: TaskRole[]): boolean {
    return matchesTaskAccessRole(taskId, roles, this.taskRoles);
  }
}
